import { setLoadPanelVisible } from "../../redux/shareSlice";
import { store } from "../../store/store";
import { Constants } from "../constants";
import { DateTimeFormatStrings } from "../enums";
import { NameValuePair } from "../models/NameValuePair";

export class Utilities {
  static isNullOrEmptyString(value: string) {
    return value === undefined || value === null || value === "";
  }

  static dataClone(data: any) {
    return JSON.parse(JSON.stringify(data));
  }

  static fileDownload = (fileData: any, fileName: string, fileType: string) => {
    if (fileData === undefined) {
      return;
    }

    const url = window.URL.createObjectURL(new Blob([fileData], { type: fileType }));
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  static blobFileDownload = (response: any) => {
    let filename = "downloadedFile.xlsx"; // default filename
    const contentDisposition = response.headers["content-disposition"];
    if (contentDisposition) {
      const filenameMatch = this.getFileNameFromContentDisposition(contentDisposition);
      if (filenameMatch) {
        filename = filenameMatch;
      }
    }

    var contentType = response.headers["content-type"];
    var fileData = response.data;

    // Check if the response is an ArrayBuffer (binary data)
    if (fileData instanceof ArrayBuffer) {
      // Convert ArrayBuffer to string to check if it's Base64 encoded
      const bytes = new Uint8Array(fileData);
      let binaryString = "";
      for (let i = 0; i < Math.min(100, bytes.length); i++) {
        binaryString += String.fromCharCode(bytes[i]);
      }

      // If it starts with a Base64 pattern, it's likely Base64 encoded
      if (binaryString.match(/^[A-Za-z0-9+/=]+$/)) {
        // Convert ArrayBuffer to string
        const decoder = new TextDecoder("utf-8");
        const base64String = decoder.decode(fileData);
        fileData = this.b64toBlob(base64String, contentType);
      }
    } else if (typeof fileData === "string" && fileData.match(/^[A-Za-z0-9+/=]+$/)) {
      // Handle direct Base64 string
      fileData = this.b64toBlob(fileData, contentType);
    }

    this.fileDownload(fileData, filename, contentType);
    return filename;
  };

  static getFileNameFromContentDisposition = (contentDisposition: string) => {
    if (!contentDisposition) return null;

    // Try "filename=" format
    let matches = contentDisposition.match(/filename="(.+)"/i);

    // If that doesn't work, try "filename*=" format
    if (!matches) {
      matches = contentDisposition.match(/filename\*=UTF-8''(.+)/i);
    }

    // If that doesn't work, try simple "filename=" format without quotes
    if (!matches) {
      matches = contentDisposition.match(/filename=(.+)/i);
    }

    if (matches && matches[1]) {
      // Decode the filename (handle URL encoding)
      return decodeURIComponent(matches[1].replace(/['"]/g, ""));
    }

    return null;
  };

  static tryServiceCallsWithLoading = async (failedAction: (error: any) => any, ...actions: (() => Promise<any>)[]) => {
    try {
      store.dispatch(setLoadPanelVisible(true));
      return await Promise.all(
        actions.map(async (action) => {
          return await action();
        })
      );
    } catch (error) {
      return failedAction(error);
    } finally {
      store.dispatch(setLoadPanelVisible(false));
    }
  };

  static dateComparison = (dateA: Date, dateB: Date) => {
    if (dateA < dateB) {
      return 1;
    } else if (dateA > dateB) {
      return -1;
    } else {
      return 0;
    }
  };

  static compareBasic = (a: any, b: any, desc: boolean) => {
    // This is for date column
    if (a instanceof Date) a = a.getTime();
    if (b instanceof Date) b = b.getTime();
    if (typeof a === "undefined") return desc ? -1 : 1;
    if (typeof b === "undefined") return desc ? 1 : -1;
    // Lowercase comparison only
    a = a.toString().toLowerCase();
    b = b.toString().toLowerCase();
    if (a === b) return 0;
    // This is to maintain order between '', null, undefined
    if (Utilities.isNullOrEmptyString(a) && Utilities.isNullOrEmptyString(b)) {
      const rankA = Constants.SORT_RANKS.findIndex((r) => r === a);
      const rankB = Constants.SORT_RANKS.findIndex((r) => r === b);
      const order = rankA - rankB;
      return desc ? -order : order;
    }
    // This is to keep excel like behaviour, empty values in bottom
    if (Utilities.isNullOrEmptyString(a)) return desc ? -1 : 1;
    if (Utilities.isNullOrEmptyString(b)) return desc ? 1 : -1;
    return a > b ? 1 : -1;
  };

  static b64toBlob = (b64Data: any, contentType = "", sliceSize = 512) => {
    const byteCharacters = atob(b64Data);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize);

      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }

    const blob = new Blob(byteArrays, { type: contentType });
    return blob;
  };

  static getTimeZone = () => {
    const timezoneOffset = new Date().getTimezoneOffset();
    const offset = Math.abs(timezoneOffset);
    const offsetOperator = timezoneOffset < 0 ? "+" : "-";
    const offsetHours = Math.floor(offset / 60)
      .toString()
      .padStart(2, "0");
    const offsetMinutes = Math.floor(offset % 60)
      .toString()
      .padStart(2, "0");

    return `${offsetOperator}${offsetHours}:${offsetMinutes}`;
  };

  static padTo2Digits = (num: number) => {
    return num.toString().padStart(2, "0");
  };

  static formatDate = (date: Date, format: DateTimeFormatStrings): string => {
    //Make sure the [date] passed in is truly a javascript Date object
    //Because all the follow up logics are based on this assumption.
    //Use new Date() to convert potential Moment Date object into javascript Date object.
    date = new Date(date);
    switch (format) {
      case DateTimeFormatStrings.FORMAT_1:
        return [date.getFullYear(), this.padTo2Digits(date.getMonth() + 1), this.padTo2Digits(date.getDate())].join("-");
      case DateTimeFormatStrings.FORMAT_2:
        return [this.padTo2Digits(date.getMonth() + 1), date.getFullYear()].join("-");
      case DateTimeFormatStrings.FORMAT_3:
        return date.toLocaleDateString("en-GB").replace(/\//g, ".");
      case DateTimeFormatStrings.FORMAT_4:
        return date.toLocaleDateString("en-GB");
      case DateTimeFormatStrings.FORMAT_5:
        return date.getFullYear() + this.padTo2Digits(date.getMonth() + 1);
      case DateTimeFormatStrings.FORMAT_6:
        return `${date.getFullYear()}-${this.padTo2Digits(date.getMonth() + 1)}-${this.padTo2Digits(date.getDate())}`;
      case DateTimeFormatStrings.FORMAT_7:
        return `${date.getFullYear()}${this.padTo2Digits(date.getMonth() + 1)}${this.padTo2Digits(date.getDate())}`;
      case DateTimeFormatStrings.FORMAT_8:
        return `${this.padTo2Digits(date.getHours())}:${this.padTo2Digits(date.getMinutes())}:${this.padTo2Digits(date.getSeconds())}`;
      case DateTimeFormatStrings.FORMAT_9:
        return `${this.formatDate(date, DateTimeFormatStrings.FORMAT_1)} ${this.formatDate(date, DateTimeFormatStrings.FORMAT_8)}${Utilities.getTimeZone()}`;
      case DateTimeFormatStrings.FORMAT_10:
        return `${this.formatDate(date, DateTimeFormatStrings.FORMAT_1)}T00:00:00Z`;
      case DateTimeFormatStrings.FORMAT_11:
        return `${this.formatDate(date, DateTimeFormatStrings.FORMAT_1)}T${this.formatDate(date, DateTimeFormatStrings.FORMAT_8)}Z`;
      case DateTimeFormatStrings.FORMAT_12:
        return `${this.formatDate(date, DateTimeFormatStrings.FORMAT_1)} ${this.formatDate(date, DateTimeFormatStrings.FORMAT_8)}`;
      case DateTimeFormatStrings.FORMAT_13:
        return [this.padTo2Digits(date.getMonth() + 1), this.padTo2Digits(date.getDate()), date.getFullYear()].join("/");
      case DateTimeFormatStrings.FORMAT_14:
        return date.toLocaleString("en-US", {
          month: "2-digit",
          day: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: true,
        });
      default:
        return date.toLocaleDateString();
    }
  };

  static capitalizeFirstLetter = (str: string) => {
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  static transformObjectToCapitalizeFirstLetter = (obj: any) => {
    return Object.fromEntries(Object.entries(obj).map(([key, value]) => [this.capitalizeFirstLetter(key), value]));
  };

  static convertObjectToNameValuePair = (obj: any, ignoreKeys: string[] = []) => {
    var result = [] as NameValuePair[];
    if (!obj) {
      return result;
    }

    Object.keys(obj).forEach((key) => {
      if (ignoreKeys.includes(key)) {
        return;
      }

      result.push({ name: key, value: obj[key] } as NameValuePair);
    });

    return result;
  };
}
